import { c as _c } from "react/compiler-runtime";
import { IcoInfo24 } from '@onefootprint/icons';
import { DocumentRequestKind, type DocumentRequirement, DocumentUploadSettings } from '@onefootprint/types';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { getLogger } from '@/idv/utils';
import type { NavigationHeaderLeftButtonProps } from '../../../../components';
import { HeaderTitle, NavigationHeader, useLayoutOptions } from '../../../../components';
import useProcessImage, { isProcessingResultError } from '../../hooks/use-process-image';
import type { CameraErrorCause, CaptureKind, ReceivedImagePayload } from '../../types';
import { isDesktop, isDocument, isFace, isMobile } from '../../utils/capture';
import Camera from '../camera';
import AutoCaptureDoc from '../camera/auto-capture-doc';
import AutoCaptureFace from '../camera/auto-capture-face';
import type { AutoCaptureKind, CaptureStatus, DeviceKind } from '../camera/types';
import type { CameraSide } from '../camera/utils/get-camera-options';
import Loading from '../loading';
import Instructions from './components/instructions';
import Preview from './components/preview';
import useCaptureTelemetry from './hooks/use-capture-telemetry';
type HeaderTextType = {
  camera: string;
  preview: string;
};
type PhotoCaptureProps = {
  autocaptureKind: AutoCaptureKind;
  cameraKind: CameraSide;
  deviceKind: DeviceKind;
  allowPdf: boolean;
  sideName?: string;
  docName?: string;
  /** Untranslated document side, for telemetry. sideName is localized and unsafe to record. */
  side?: string;
  requirement: DocumentRequirement;
  hasBadConnectivity?: boolean;
  onCameraStuck?: () => void;
  onCameraErrored?: (cause: CameraErrorCause) => void;
  onCameraAccessDenied?: () => void;
  onBack?: () => void;
  onComplete: (payload: ReceivedImagePayload) => void;
  outlineHeightRatio: number; // with respect to the video width (not height)
  outlineWidthRatio: number; // with respect to the video width
  subtitle?: Partial<HeaderTextType>;
  title: HeaderTextType;
};
const DesktopNavProps: NavigationHeaderLeftButtonProps = {
  variant: 'close',
  confirmClose: true
};
const {
  logWarn,
  logInfo
} = getLogger({
  location: 'photo-capture'
});
const PhotoCapture = t0 => {
  const $ = _c(68);
  const {
    autocaptureKind,
    cameraKind,
    deviceKind,
    allowPdf,
    sideName,
    docName,
    side,
    requirement,
    hasBadConnectivity,
    onCameraStuck,
    onCameraErrored,
    onCameraAccessDenied,
    onBack,
    onComplete,
    outlineHeightRatio,
    outlineWidthRatio,
    subtitle,
    title
  } = t0;
  const allowUpload = requirement.uploadSettings !== DocumentUploadSettings.captureOnlyOnMobile && requirement.uploadSettings !== DocumentUploadSettings.requireMobileCapture;
  let t1;
  if ($[0] !== autocaptureKind || $[1] !== requirement.config) {
    t1 = isFace(autocaptureKind) && requirement.config.kind === DocumentRequestKind.Identity && requirement.config.collectSelfieVideo === "best_effort";
    $[0] = autocaptureKind;
    $[1] = requirement.config;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const recordVideo = t1;
  let t2;
  if ($[3] !== allowPdf) {
    t2 = {
      allowPdf
    };
    $[3] = allowPdf;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    processImageUrl
  } = useProcessImage(t2);
  const [image, setImage] = useState(null);
  const video = useRef(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [captureKind, setCaptureKind] = useState();
  const [showInstructions, setShowInstructions] = useState(false);
  const [isCaptured, setIsCaptured] = useState(false);
  const [autocaptureFeedback, setAutocaptureFeedback] = useState("detecting");
  let t3;
  if ($[5] !== autocaptureFeedback || $[6] !== autocaptureKind || $[7] !== side) {
    t3 = {
      autocaptureKind,
      side,
      status: autocaptureFeedback
    };
    $[5] = autocaptureFeedback;
    $[6] = autocaptureKind;
    $[7] = side;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const {
    reportCapture,
    reportCameraStuck
  } = useCaptureTelemetry(t3);
  const {
    footer: t4,
    body: t5
  } = useLayoutOptions();
  const {
    set: updateFooter
  } = t4;
  const {
    set: updateBody
  } = t5;
  let t6;
  let t7;
  if ($[9] !== deviceKind || $[10] !== image || $[11] !== updateFooter) {
    t6 = () => {
      if (isMobile(deviceKind)) {
        return _temp;
      }
      if (image) {
        updateFooter({
          visible: true
        });
      } else {
        updateFooter({
          visible: false
        });
      }
      return () => {
        updateFooter({
          visible: true
        });
      };
    };
    t7 = [image, deviceKind, updateFooter];
    $[9] = deviceKind;
    $[10] = image;
    $[11] = updateFooter;
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  useLayoutEffect(t6, t7);
  let t8;
  let t9;
  if ($[14] !== deviceKind || $[15] !== image || $[16] !== updateBody) {
    t8 = () => {
      if (!isMobile(deviceKind)) {
        return _temp2;
      }
      updateBody({
        fullBleed: !image
      });
      return () => {
        updateBody({
          fullBleed: false
        });
      };
    };
    t9 = [image, deviceKind, updateBody];
    $[14] = deviceKind;
    $[15] = image;
    $[16] = updateBody;
    $[17] = t8;
    $[18] = t9;
  } else {
    t8 = $[17];
    t9 = $[18];
  }
  useLayoutEffect(t8, t9);
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      logInfo("Retaking photo");
      setImage(null);
      video.current = undefined;
      setIsCaptured(false);
    };
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const handleRetake = t10;
  let t11;
  if ($[20] !== captureKind || $[21] !== hasBadConnectivity || $[22] !== image || $[23] !== onComplete || $[24] !== processImageUrl) {
    t11 = async () => {
      if (!image) {
        logWarn("Captured image could not be confirmed and submitted - retaking the image");
        return;
      }
      if (!captureKind) {
        logWarn("Captured kind could not be determined - retaking the image");
        return;
      }
      setIsLoading(true);
      const processResult = await processImageUrl(image, hasBadConnectivity);
      if (isProcessingResultError(processResult)) {
        setIsLoading(false);
        handleRetake();
        logWarn("Captured image could not be processed - retaking the image");
        return;
      }
      const videoFile = await video.current;
      setIsLoading(false);
      const {
        file,
        extraCompressed
      } = processResult;
      onComplete({
        captureKind,
        extraCompressed,
        imageFile: file,
        forcedUpload: false,
        videoFile
      });
    };
    $[20] = captureKind;
    $[21] = hasBadConnectivity;
    $[22] = image;
    $[23] = onComplete;
    $[24] = processImageUrl;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const handleConfirm = t11;
  let t12;
  if ($[26] !== onCameraErrored) {
    t12 = cause => {
      logInfo("Camera error occurred", {
        cause
      });
      onCameraErrored?.(cause);
    };
    $[26] = onCameraErrored;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const handleError = t12;
  let t13;
  if ($[28] !== deviceKind || $[29] !== handleConfirm || $[30] !== image) {
    t13 = () => {
      if (image && isMobile(deviceKind)) {
        handleConfirm();
      }
    };
    $[28] = deviceKind;
    $[29] = handleConfirm;
    $[30] = image;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== image) {
    t14 = [image];
    $[32] = image;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  useEffect(t13, t14);
  let t15;
  if ($[34] !== reportCapture) {
    t15 = (newImage, newCaptureKind, newVideo) => {
      reportCapture(newCaptureKind);
      setImage(newImage);
      video.current = newVideo;
      setCaptureKind(newCaptureKind);
    };
    $[34] = reportCapture;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  const handleCapture = t15;
  let t16;
  if ($[36] !== onComplete || $[37] !== reportCapture) {
    t16 = payload => {
      reportCapture(payload.captureKind);
      onComplete(payload);
    };
    $[36] = onComplete;
    $[37] = reportCapture;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  const handleUpload = t16;
  let t17;
  if ($[39] !== onCameraStuck || $[40] !== reportCameraStuck) {
    t17 = () => {
      reportCameraStuck();
      onCameraStuck?.();
    };
    $[39] = onCameraStuck;
    $[40] = reportCameraStuck;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  const handleCameraStuck = t17;
  let t18;
  if ($[42] !== allowPdf || $[43] !== allowUpload || $[44] !== autocaptureFeedback || $[45] !== autocaptureKind || $[46] !== cameraKind || $[47] !== deviceKind || $[48] !== docName || $[49] !== handleCameraStuck || $[50] !== handleCapture || $[51] !== handleConfirm || $[52] !== handleError || $[53] !== handleUpload || $[54] !== hasBadConnectivity || $[55] !== image || $[56] !== isCaptured || $[57] !== isLoading || $[58] !== onBack || $[59] !== onCameraAccessDenied || $[60] !== outlineHeightRatio || $[61] !== outlineWidthRatio || $[62] !== recordVideo || $[63] !== showInstructions || $[64] !== sideName || $[65] !== subtitle?.camera || $[66] !== title) {
    t18 = image ? <>{isDesktop(deviceKind) && <><div className="mb-6"><NavigationHeader leftButton={DesktopNavProps} /><HeaderTitle title={title.preview} /></div><Preview imageSrc={image} onRetake={handleRetake} onConfirm={handleConfirm} isLoading={isLoading} cameraKind={cameraKind} deviceKind={deviceKind} /></>}{isMobile(deviceKind) && <div className="flex flex-col items-center justify-center h-full"><Loading step="process" /></div>}</> : <>{isDesktop(deviceKind) && <div className="mb-6"><NavigationHeader leftButton={DesktopNavProps} /><HeaderTitle title={title.camera} subtitle={subtitle?.camera} /></div>}{isMobile(deviceKind) && <NavigationHeader leftButton={{
        variant: "back",
        onBack
      }} rightButton={{
        icon: IcoInfo24,
        onClick: () => setShowInstructions(true)
      }} content={{
        kind: "static",
        title: title.camera
      }} />}<Camera autoCaptureFeedback={autocaptureFeedback} autoCaptureKind={autocaptureKind} cameraSide={cameraKind} deviceKind={deviceKind} docName={docName} sideName={sideName} onCapture={handleCapture} onUpload={handleUpload} recordVideo={recordVideo} hasBadConnectivity={hasBadConnectivity} onCameraError={isMobile(deviceKind) ? handleError : undefined} onCameraAccessDenied={onCameraAccessDenied} outlineHeightRatio={outlineHeightRatio} outlineWidthRatio={outlineWidthRatio} setIsCaptured={setIsCaptured} allowPdf={allowPdf} onCameraStuck={handleCameraStuck} allowUpload={allowUpload}>{t19 => {
          const {
            canvasAutoCaptureRef,
            feedbackPositionFromBottom,
            videoResolution,
            onDetectionComplete,
            onDetectionReset,
            outlineHeight,
            outlineWidth,
            videoRef,
            videoSize
          } = t19;
          if (isDocument(autocaptureKind)) {
            return <AutoCaptureDoc canvasAutoCaptureRef={canvasAutoCaptureRef} feedbackPositionFromBottom={feedbackPositionFromBottom} isCaptured={isCaptured} videoResolution={videoResolution} onDetectionComplete={onDetectionComplete} onDetectionReset={onDetectionReset} outlineHeight={outlineHeight} outlineWidth={outlineWidth} setAutoCaptureFeedback={setAutocaptureFeedback} videoRef={videoRef} videoSize={videoSize} />;
          }
          return isFace(autocaptureKind) ? <AutoCaptureFace canvasAutoCaptureRef={canvasAutoCaptureRef} feedbackPositionFromBottom={feedbackPositionFromBottom} isCaptured={isCaptured} onDetectionComplete={onDetectionComplete} onDetectionReset={onDetectionReset} outlineWidth={outlineWidth} setAutoCaptureFeedback={setAutocaptureFeedback} videoRef={videoRef} videoSize={videoSize} videoResolution={videoResolution} /> : null;
        }}</Camera><Instructions onClose={() => setShowInstructions(false)} isOpen={showInstructions} autoCaptureKind={autocaptureKind} /></>;
    $[42] = allowPdf;
    $[43] = allowUpload;
    $[44] = autocaptureFeedback;
    $[45] = autocaptureKind;
    $[46] = cameraKind;
    $[47] = deviceKind;
    $[48] = docName;
    $[49] = handleCameraStuck;
    $[50] = handleCapture;
    $[51] = handleConfirm;
    $[52] = handleError;
    $[53] = handleUpload;
    $[54] = hasBadConnectivity;
    $[55] = image;
    $[56] = isCaptured;
    $[57] = isLoading;
    $[58] = onBack;
    $[59] = onCameraAccessDenied;
    $[60] = outlineHeightRatio;
    $[61] = outlineWidthRatio;
    $[62] = recordVideo;
    $[63] = showInstructions;
    $[64] = sideName;
    $[65] = subtitle?.camera;
    $[66] = title;
    $[67] = t18;
  } else {
    t18 = $[67];
  }
  return t18;
};
export default PhotoCapture;
function _temp() {}
function _temp2() {}